import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import {AttrsSchema, Component, jsx} from 'panel';
import type {VNode} from 'snabbdom';
import ResizeObserver from 'resize-observer-polyfill';
import debounce from 'lodash/debounce';

import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {SpinnerEl} from 'common/components/spinner';
import {SegmentLegendHeaderEl} from 'common/charts/misc/segment-legend-header';
import {CountType, FunnelSegment, SegmentId} from 'common/report/funnels/models/types';
import {
  BarSelection,
  ConversionColumnViz,
  ConversionColumnVizEl,
  StepBarClickEvent,
  StepHorizontalWheelEvent,
} from 'common/charts/visualization/conversion-column-viz';
import {ChartType, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {EmptyDataEl} from 'common/charts/misc/empty-data';
import {
  ConversionStepBar,
  getPercentLabel,
  NormalConversionStep,
} from 'common/charts/visualization/conversion-column-viz/util';
import {ChartTooltipAttrs} from 'common/components/chart-tooltip';
import {TooltipBulletStyle, TooltipItemType, TooltipRow} from 'common/components/chart-tooltip/types';
import {commaizeNumber} from 'common/util';
import {ScrollControlEl} from 'common/widgets/scroll-control';
import {CalendarDateRange} from 'common/widgets/date-range-picker/models/types';
import {CurrentProject, CurrentProjectResource} from 'common/report/model-resources/projects';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {OVERALL_PROP} from 'common/report/funnels/models/constants';
import {ChartPopover} from 'common/report/funnels/visualization/chart-display/utils/popover';
import {calculatePopoverPosition} from 'common/report/funnels/util/positioning';
import type {
  PopoverParams,
  StepPopoverAction,
  StepPopoverActionParams,
} from 'common/report/funnels/visualization/chart-display';
import {dataGroupStore} from 'common/report/stores/data-groups';

import css from './index.inline.styl';
import {
  FunnelsStepProjectDetails,
  getBreakdownSegmentProp,
  getComparisonBarIndex,
  getLegendItemsWithDataTableRows,
  getMainBarIndex,
  OVERALL_CONTROL_GROUP,
  StepChartData,
} from './util';
import {BarType, BookmarkData, FunnelStepBookmarkType, FunnelStepSegmentSelection} from './types';
import {StepChartBookmarkController} from './bookmark-controller';
import {MultimetricStepBookmarkController} from './bookmark-controller/multimetric';
import {FunnelsStepChartBookmarkController} from './bookmark-controller/funnels';
import {StepsChartPopover} from './mixins';

export type SegmentClickEvent = CustomEvent<{
  stepIndex: number;
  segmentProp: string;
  isDropoff: boolean;
  isComparison: boolean;
  comparisonDateRange: CalendarDateRange | null;
  color: string;
  originalMouseEvent: MouseEvent;

  // Including the whole collection of FunnelSegment objects is a hack because the Funnels app requires a coherent collection of FunnelSegments to show a popover.
  // Ideally, this Funnels Step Chart widget will render popovers itself, but there's too much popover logic in Funnels app to pragmatically migrate right now.
  segments: FunnelSegment[];
}>;

export type ChangeControlGroupEvent = CustomEvent<{
  controlGroup: SegmentId[];
}>;

export type FunnelStepTrackEvent = CustomEvent<{
  eventName: string;
  properties: Obj;
}>;

interface FunnelsStepChartState {
  stepChartData: StepChartData | null;
  popoverOpen: boolean;
  popoverParams: PopoverParams;
}
interface FunnelsStepChartAttrs {
  loaded: boolean;
  failed?: boolean;
  'show-popover'?: boolean;
  'enable-segment-click'?: boolean;
  'min-step-width'?: number;
  'max-step-width'?: number;
  'resizing-layout'?: boolean;
}

export interface FunnelsStepChartJsxProps {
  attrs: FunnelsStepChartAttrs;
  props: {
    selectedSegment?: null | FunnelStepSegmentSelection;
    bookmarkData?: BookmarkData;
    popoverCallback?: (params: StepPopoverActionParams) => void;
    // project details like projectAsOfDate are typically attributes instead of an object prop, but projectAsOfDate supports null/empty as a valid value
    // which makes it impractical for a 'project-as-of-date' attribute to distinguish between "set to null/empty" and "not set, default to Store"
    projectDetails?: FunnelsStepProjectDetails | null;
  };
  on?: {
    segmentClick?: (ev: SegmentClickEvent) => void;
    changeControlGroup?: (ev: ChangeControlGroupEvent) => void;
    track?: (ev: FunnelStepTrackEvent) => void;
  };
}

export class FunnelsStepChart extends Component<
  FunnelsStepChartState,
  FunnelsStepChartAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  static get attrsSchema(): AttrsSchema<FunnelsStepChartAttrs> {
    return {
      ...super.attrsSchema,
      loaded: `boolean`,
      failed: `boolean`,
      'enable-segment-click': `boolean`,
      'min-step-width': `number`,
      'max-step-width': `number`,
      'resizing-layout': `boolean`,
      'show-popover': `boolean`,
    };
  }

  private _popoverCallback: (params: StepPopoverActionParams) => void = null;

  private _bookmarkController: StepChartBookmarkController;
  public set bookmarkData(data: BookmarkData) {
    if (this._bookmarkController && this._bookmarkController.isEquivalent(data.bookmark, data.queryResults)) {
      return;
    }
    switch (data.type) {
      case FunnelStepBookmarkType.MULTIMETRIC:
        this._bookmarkController = new MultimetricStepBookmarkController(data.bookmark, data.queryResults);
        break;
      case FunnelStepBookmarkType.FUNNELS:
        this._bookmarkController = new FunnelsStepChartBookmarkController(data.bookmark, data.queryResults);
        break;
    }
    this.updateStepChartData();
  }

  private _projectDetails: FunnelsStepProjectDetails | null = null;
  public get projectDetails(): FunnelsStepProjectDetails | null {
    return this._projectDetails;
  }
  public set projectDetails(value: FunnelsStepProjectDetails | null) {
    if (!isEqual(this._projectDetails, value)) {
      this._projectDetails = value;
      this.updateStepChartData();
    }
  }

  private _selectedSegment: FunnelStepSegmentSelection | null = null;
  public get selectedSegment(): FunnelStepSegmentSelection | null {
    return this._selectedSegment;
  }
  public set selectedSegment(value: FunnelStepSegmentSelection | null) {
    this._selectedSegment = value;
    this.update();
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  public set popoverCallback(popoverCallback: (param: StepPopoverActionParams) => void) {
    this._popoverCallback = popoverCallback;
  }

  private selfResizeObserver?: ResizeObserver;
  private selfWidth = 0;
  private chartEl?: ConversionColumnViz;

  get config() {
    return {
      useShadowDom: true,
      css,
      template: () => this.renderTemplate(),
      defaultState: {
        stepChartData: null,
        popoverOpen: false,
        popoverParams: null,
      },
      contexts: [`segmentFilterController` as const, `currentProject` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
    };
  }

  connectedCallback() {
    const debouncedUpdateLegend = debounce(this.updateLegend.bind(this), 100, {leading: true});
    this.selfResizeObserver = new ResizeObserver((entries) => {
      this.selfWidth = entries[0].contentRect.width;
      debouncedUpdateLegend();
    });
    this.selfResizeObserver.observe(this);

    super.connectedCallback();
    this.updateStepChartData();
  }

  disconnectedCallback() {
    this.selfResizeObserver.disconnect();
    super.disconnectedCallback();
  }

  resourceCustomSubscriptions = new Map([
    [CurrentProjectResource, () => !this.projectDetails && this.updateStepChartData()],
  ]);

  private getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  getActiveDataGroup() {
    const activeDataGroupId = this._bookmarkController.getDataGroupId();

    if (activeDataGroupId && dataGroupStore.value) {
      return dataGroupStore.value[activeDataGroupId];
    }
    return null;
  }

  private getPopoverParams(
    {detail: {originalMouseEvent, isDropoff, color, isComparison, stepIndex, barIndex}}: StepBarClickEvent,
    segmentProp: string,
  ) {
    const {
      comparisonDateRange,
      comparisonDateRangeLabel,
      countType,
      funnelSegmentsForPopover,
    } = this.state.stepChartData;
    const bar = {
      type: isDropoff ? BarType.DROP_OFF : BarType.CONVERTED,
      isComparison,
      comparisonDateRange,
      color,
      index: stepIndex,
      barIdx: barIndex,
    };
    this.selectedSegment = {
      stepIndex,
      disableTooltips: false,
      isDropoff,
      segmentProp,
    };
    const dataGroup = this.getActiveDataGroup();
    const popover = new ChartPopover(
      funnelSegmentsForPopover,
      bar,
      {
        canViewInFlows: true,
        dataGroupName: dataGroup?.displayName ?? `Users`,
        dataGroup,
      },
      !!comparisonDateRangeLabel,
    );
    const styles = calculatePopoverPosition({
      barElement: originalMouseEvent.target as HTMLElement,
      viewerElement: this,
    });
    return {
      open: true,
      bar,
      // cohort: cohortDetails[0].cohort, TODO cohorts
      // cohortDetails,
      groupByValue: this.state.stepChartData.conversionSteps[bar.index].displayName,
      segmentProp,
      styles,
      isSegmented: popover.isSegmented(),
      isDropOff: popover.isDropOff(),
      title: popover.getTitle(this.state.stepChartData.conversionSteps.map((step) => step.displayName)),
      buttonOptions: popover.getButtonOptions(),
      slottedContent: popover.getSlottedContent(countType),
    };
  }

  private renderTemplate(): VNode {
    const loaded = this.attr(`loaded`) && this.hasRequiredParams() && !!this.state.stepChartData;
    if (!loaded) {
      return (
        <div sel='.root'>
          <SpinnerEl attrs={{elref: `loadingSpinner`}} />
        </div>
      );
    }

    if (this._bookmarkController.isEmptyReport()) {
      return (
        <div sel='.root'>
          <EmptyDataEl />
        </div>
      );
    }

    return (
      <div sel='.root' class={{'resizing-layout': this.attr(`resizing-layout`)}}>
        {this.state.popoverOpen && (
          <StepsChartPopover
            popoverParams={{
              open: true,
              ...this.state.popoverParams,
              onAction: (ev) =>
                this._popoverCallback?.({
                  action: ev.detail.action as StepPopoverAction,
                  stepIndex: this.selectedSegment.stepIndex,
                }),
              onClose: () => this.update({popoverOpen: false}),
            }}
          />
        )}
        <SegmentLegendHeaderEl sel='.legend-header' />
        <ConversionColumnVizEl
          sel='.step-chart'
          attrs={{
            elref: `conversionColumnViz`,
            'enable-bar-click': this.attr(`enable-segment-click`),
            ...(this.attr(`min-step-width`) ? {'min-step-width': this.attr(`min-step-width`)} : {}),
            ...(this.attr(`max-step-width`) ? {'max-step-width': this.attr(`max-step-width`)} : {}),
          }}
          props={{
            conversionSteps: this.getVisibleConversionSteps(),
            selectedBars: this.getSelectedStepBars(),
            stepBarTooltipCallback: this.getTooltipData.bind(this),
          }}
          on={{
            stepBarClick: this.handleStepBarClick.bind(this),
            stepHorizontalWheel: this.handleStepHorizontalWheel.bind(this),
          }}
          hook={{
            insert: (vnode: VNode): void => {
              this.chartEl = vnode.elm as ConversionColumnViz;
              this.update(); // note: chartEl is used to render <ScrollControlEl>
            },
            remove: (_, removeCallback: () => void): void => {
              delete this.chartEl;
              removeCallback();
            },
          }}
        />
        <ScrollControlEl
          sel='.scroll-control'
          attrs={{
            'scroll-by': this.chartEl?.getStepWidth(),
            left: `${this.getScrollControlLeftOffset()}px`,
          }}
          props={{
            scrollEl: this.getChartScrollContainer(),
            mousemoveEl: this,
          }}
        />
      </div>
    );
  }

  private getProjectDetails(): null | FunnelsStepProjectDetails {
    if (this.projectDetails) {
      return this.projectDetails;
    }
    const currentProject = this.getCurrentProject();
    if (currentProject) {
      return {
        projectId: currentProject.id.toString(),
        projectAsOfDate: currentProject.projectAsOfDate,
        projectTimeZone: currentProject.timezone,
      };
    }
    return null;
  }

  private hasRequiredParams(): boolean {
    return !!(this.isConnected && this._bookmarkController && this.getProjectDetails());
  }

  private updateStepChartData(): void {
    if (this.hasRequiredParams()) {
      const stepChartData = this._bookmarkController.getStepChartData({
        projectDetails: this.getProjectDetails(),
        segmentFilterController: this.segmentFilterController,
      });

      this.update({stepChartData});
      this.updateLegend();
    } else {
      this.update({stepChartData: null});
    }
  }

  private getSelectedStepBars(): BarSelection[] {
    const stepChartData = this.state.stepChartData;
    const selectedSegment = this.selectedSegment;
    const selections: BarSelection[] = [];
    if (selectedSegment && stepChartData) {
      const conversionStep = this.getVisibleConversionSteps()[selectedSegment.stepIndex];
      selections.push({
        stepIndex: selectedSegment.stepIndex,
        barIndex: getMainBarIndex(conversionStep, selectedSegment.segmentProp),
        isDropoff: selectedSegment.isDropoff,
      });

      const hasComparisonData = !!stepChartData.comparisonDateRangeLabel;
      if (hasComparisonData) {
        selections.push({
          stepIndex: selectedSegment.stepIndex,
          barIndex: getComparisonBarIndex(conversionStep, selectedSegment.segmentProp),
          isDropoff: selectedSegment.isDropoff,
        });
      }
    }
    return selections;
  }

  private getVisibleConversionSteps(): Array<NormalConversionStep<never, string>> {
    // TODO this does nothing specific to the bookmark type, move it back to the abstract file once multimetric is more mature
    return (
      this._bookmarkController
        .getStepChartData({
          projectDetails: this.getProjectDetails(),
          segmentFilterController: this.segmentFilterController,
        })
        ?.conversionSteps?.map(
          (step): NormalConversionStep<never, string> => {
            const barLookup = new Map<string, ConversionStepBar[]>();
            step.bars.forEach((bar) => {
              const segmentKey = bar.extras;
              if (segmentKey || !isNil(bar.displayName)) {
                if (!barLookup.has(segmentKey)) {
                  barLookup.set(segmentKey, []);
                }
                barLookup.get(segmentKey).push(bar);
              }
            });
            return {
              ...step,
              bars: this.segmentFilterController
                .getSelectedItems(0)
                .filter((item) => barLookup.has(item.id))
                .flatMap((item) => {
                  const bars = barLookup.get(item.id);
                  return bars.map((bar) => ({
                    ...bar,
                    color: this.segmentFilterController.getItemColor(0, bar.extras),
                  }));
                }),
            };
          },
        ) ?? []
    );
  }

  private updateLegend(): void {
    const {stepChartData} = this.state;
    if (stepChartData) {
      const {rows, comparisonRows, columnGroups} = this._bookmarkController.getDataTableInfo({
        stepChartData,
        projectDetails: this.getProjectDetails(),
        tableWidth: this.selfWidth,
        onStatSigClick: (_, row) => {
          if (row.props[0].prop === OVERALL_PROP) {
            this.dispatchEvent(new CustomEvent(`changeControlGroup`, {detail: {props: OVERALL_CONTROL_GROUP}}));
          } else {
            this.dispatchEvent(new CustomEvent(`changeControlGroup`, {detail: {props: row.props}}));
          }
        },
      });

      const legendSegmentItems = stepChartData.detailedLegendSegmentItems;
      this.segmentFilterController.loadSections(
        [
          {
            items: getLegendItemsWithDataTableRows({
              rows,
              comparisonRows,
              legendSegmentItems,
            }),
            label: stepChartData.legendSegmentLabel,
            tableColumns: columnGroups,
          },
        ],
        {
          tableSortConfig: this._bookmarkController.getSorting(),
          defaultNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelSteps].default,
          maxNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelSteps].max,
        },
      );
      this.segmentFilterController.loadFromSavedLegend(stepChartData.savedLegend);
      this.segmentFilterController.segmentationQueryLimit = REPORT_LIMITS[ReportName.Funnels].query;
      this.segmentFilterController.exportSegmentationQueryLimit = REPORT_LIMITS[ReportName.Funnels].csvExport;

      this.update();
    }
  }

  private getSingleBarTooltipRows(
    conversionStep: NormalConversionStep<never, string>,
    previousConversionStep: null | NormalConversionStep<never, string>,
    barIndex: number,
    hoveredOnDropoff: boolean,
  ): TooltipRow[] {
    const bar = conversionStep.bars[barIndex];
    const percent = hoveredOnDropoff ? 1 - bar.conversionRate : bar.conversionRate;
    const percentDescription = hoveredOnDropoff ? `drop-off` : `converted`;

    const dropoffValue = previousConversionStep
      ? previousConversionStep.bars[barIndex].value - conversionStep.bars[barIndex].value
      : 0;
    const count = hoveredOnDropoff ? dropoffValue : conversionStep.bars[barIndex].value;

    let countDescription;
    switch (this.state.stepChartData.countType) {
      case CountType.Unique:
        countDescription = `uniques`;
        break;
      case CountType.Session:
        countDescription = `sessions`;
        break;
      case CountType.General:
      default:
        countDescription = `total times`;
        break;
    }

    return [
      {
        items: [
          {text: getPercentLabel(percent), type: TooltipItemType.Data},
          {text: percentDescription, type: TooltipItemType.Text},
        ],
      },
      {
        items: [
          {text: commaizeNumber(count), type: TooltipItemType.Data},
          {text: countDescription, type: TooltipItemType.Text},
        ],
      },
    ];
  }

  private getTooltipData(
    conversionStep: NormalConversionStep<never, string>,
    previousConversionStep: null | NormalConversionStep<never, string>,
    barIndex: number,
    hoveredOnDropoff: boolean,
  ): ChartTooltipAttrs {
    if (this.selectedSegment?.disableTooltips) {
      return {sections: []};
    }

    const bar = conversionStep.bars[barIndex];

    const {comparisonDateRangeLabel, mainDateRangeLabel} = this.state.stepChartData;
    const hasComparisonData = !!comparisonDateRangeLabel;
    if (hasComparisonData) {
      const breakdownSegmentProp = getBreakdownSegmentProp(conversionStep, barIndex);
      const mainBarIndex: number = getMainBarIndex(conversionStep, breakdownSegmentProp);
      const comparisonBarIndex: number = getComparisonBarIndex(conversionStep, breakdownSegmentProp);

      const mainDataRows = this.getSingleBarTooltipRows(
        conversionStep,
        previousConversionStep,
        mainBarIndex,
        hoveredOnDropoff,
      );
      const comparisonDataRows = this.getSingleBarTooltipRows(
        conversionStep,
        previousConversionStep,
        comparisonBarIndex,
        hoveredOnDropoff,
      );
      return {
        sections: [
          {
            rows: [{items: [{text: bar.displayName, type: TooltipItemType.Strong}]}],
          },
          {
            bulletColor: conversionStep.bars[mainBarIndex].color,
            bulletStyle: TooltipBulletStyle.Solid,
            rows: [{items: [{text: mainDateRangeLabel, type: TooltipItemType.Strong}]}, ...mainDataRows],
          },
          {
            bulletColor: conversionStep.bars[comparisonBarIndex].color,
            bulletStyle: TooltipBulletStyle.Dash,
            rows: [{items: [{text: comparisonDateRangeLabel, type: TooltipItemType.Strong}]}, ...comparisonDataRows],
          },
        ],
      };
    }

    const dataRows = this.getSingleBarTooltipRows(conversionStep, previousConversionStep, barIndex, hoveredOnDropoff);
    return {
      sections: [
        {
          rows: [{items: [{text: bar.displayName, type: TooltipItemType.Strong}]}],
        },
        {
          rows: dataRows,
        },
      ],
      'title-color': bar.color,
    };
  }

  private handleStepBarClick(ev: StepBarClickEvent): void {
    const {stepChartData} = this.state;
    const conversionStep = this.getVisibleConversionSteps()[ev.detail.stepIndex];
    const breakdownSegmentProp = getBreakdownSegmentProp(conversionStep, ev.detail.barIndex);
    this.update({
      popoverOpen: this.attr(`show-popover`),
      popoverParams: this.getPopoverParams(ev, breakdownSegmentProp),
    });
    this.dispatchEvent(
      new CustomEvent(`segmentClick`, {
        detail: {
          stepIndex: ev.detail.stepIndex,
          segmentProp: breakdownSegmentProp,
          isComparison: ev.detail.isComparison,
          color: ev.detail.color,
          comparisonDateRange: stepChartData.comparisonDateRange,
          isDropoff: ev.detail.isDropoff,
          originalMouseEvent: ev.detail.originalMouseEvent,
          segments: this.state.stepChartData.funnelSegmentsForPopover,
        },
      }) as SegmentClickEvent,
    );
  }

  private handleStepHorizontalWheel(ev: StepHorizontalWheelEvent): void {
    const trackEvent: FunnelStepTrackEvent = new CustomEvent(`track`, {
      detail: {
        eventName: `[Funnels] Scroll chart steps`,
        properties: {
          startIndex: ev.detail.firstVisibleStepIndex,
          direction: ev.detail.direction,
        },
      },
    });
    this.dispatchEvent(trackEvent);
  }

  public getChartScrollContainer(): null | HTMLElement {
    return this.chartEl?.getScrollContainer() ?? null;
  }

  public getScrollControlLeftOffset(): number {
    const scrollEl = this.getChartScrollContainer();
    if (!scrollEl) {
      return 0;
    }
    return scrollEl.getBoundingClientRect().left - this.getBoundingClientRect().left - 20;
  }
}

export const FunnelsStepChartEl = defineAndMakeJsxFactory<FunnelsStepChartJsxProps>(
  `mp-funnels-step-chart`,
  FunnelsStepChart,
);
export const FunnelsStepChartReact = panel2react<FunnelsStepChartJsxProps>(`mp-funnels-step-chart`, FunnelsStepChart);
